import React, { useState } from 'react';
import { Layout, ConfigProvider, Button, Space, message, Radio } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import StockSearch from './components/StockSearch';
import StockChart from './components/StockChart';
import './App.css';

const { Header, Content } = Layout;

function App() {
  const [stockData, setStockData] = useState(null);
  const [stockInfo, setStockInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [originalStockData, setOriginalStockData] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState('daily');

  /**
   * 处理模拟计算数据
   * @param {Object} simulatedResult - 模拟计算结果
   */
  const handleSimulatedData = (simulatedResult) => {
    const { data, parameters } = simulatedResult;
    setStockData(data);
    message.success(`模拟计算完成！参数: N=${parameters.n}, M1=${parameters.m1}, M2=${parameters.m2}, 最近一日成交价=${parameters.lastClosePrice}`);
  };

  /**
   * 处理股票数据变化
   * @param {Array} data - 股票数据
   * @param {Object} info - 股票信息
   */
  const handleStockDataChange = (data, info) => {
    setStockData(data);
    setStockInfo(info);
    setOriginalStockData(data);
    setPeriodData(generatePeriodData(data));
  };

  /**
   * 生成各周期数据
   * @param {Array} dailyData - 日K数据
   * @returns {Object} 各周期数据
   */
  const generatePeriodData = (dailyData) => {
    if (!dailyData || dailyData.length === 0) {
      return { daily: null, weekly: null, monthly: null, day45: null };
    }
    const sortedData = [...dailyData].sort((a, b) => new Date(a.trade_date) - new Date(b.trade_date));
    return {
      daily: sortedData,
      weekly: generateWeeklyData(sortedData),
      monthly: generateMonthlyData(sortedData),
      day45: generateDay45Data(sortedData)
    };
  };

  /**
   * 生成周K数据
   * @param {Array} dailyData - 日K数据
   * @returns {Array} 周K数据
   */
  const generateWeeklyData = (dailyData) => {
    const weeklyData = [];
    let currentWeek = null;
    
    dailyData.forEach(item => {
      const date = new Date(item.trade_date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay() + 1); // 周一
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!currentWeek || currentWeek.weekKey !== weekKey) {
        if (currentWeek) {
          weeklyData.push(currentWeek);
        }
        currentWeek = {
          weekKey,
          trade_date: item.trade_date,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
          vol: item.vol,
          amount: item.amount
        };
      } else {
        currentWeek.high = Math.max(currentWeek.high, item.high);
        currentWeek.low = Math.min(currentWeek.low, item.low);
        currentWeek.close = item.close;
        currentWeek.vol += item.vol;
        currentWeek.amount += item.amount;
      }
    });
    
    if (currentWeek) {
      weeklyData.push(currentWeek);
    }
    
    return weeklyData;
  };

  /**
   * 生成月K数据
   * @param {Array} dailyData - 日K数据
   * @returns {Array} 月K数据
   */
  const generateMonthlyData = (dailyData) => {
    const monthlyData = [];
    let currentMonth = null;
    
    dailyData.forEach(item => {
      const date = new Date(item.trade_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!currentMonth || currentMonth.monthKey !== monthKey) {
        if (currentMonth) {
          monthlyData.push(currentMonth);
        }
        currentMonth = {
          monthKey,
          trade_date: item.trade_date,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
          vol: item.vol,
          amount: item.amount
        };
      } else {
        currentMonth.high = Math.max(currentMonth.high, item.high);
        currentMonth.low = Math.min(currentMonth.low, item.low);
        currentMonth.close = item.close;
        currentMonth.vol += item.vol;
        currentMonth.amount += item.amount;
      }
    });
    
    if (currentMonth) {
      monthlyData.push(currentMonth);
    }
    
    return monthlyData;
  };

  /**
   * 生成45天K线数据
   * @param {Array} dailyData - 日K数据
   * @returns {Array} 45天K线数据
   */
  const generateDay45Data = (dailyData) => {
    const day45Data = [];
    let currentGroup = null;
    const groupSize = 45;
    
    dailyData.forEach((item, index) => {
      const groupIndex = Math.floor(index / groupSize);
      
      if (groupIndex !== currentGroup?.groupIndex) {
        if (currentGroup) {
          day45Data.push(currentGroup);
        }
        currentGroup = {
          groupIndex,
          trade_date: item.trade_date,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
          vol: item.vol,
          amount: item.amount
        };
      } else {
        currentGroup.high = Math.max(currentGroup.high, item.high);
        currentGroup.low = Math.min(currentGroup.low, item.low);
        currentGroup.close = item.close;
        currentGroup.vol += item.vol;
        currentGroup.amount += item.amount;
      }
    });
    
    if (currentGroup) {
      day45Data.push(currentGroup);
    }
    
    return day45Data;
  };

  /**
   * 切换周期
   * @param {string} period - 周期
   */
  const handlePeriodChange = (period) => {
    setCurrentPeriod(period);
    if (periodData && periodData[period]) {
      setStockData(periodData[period]);
    }
  };

  /**
   * 恢复到原始数据
   */
  const handleResetData = () => {
    if (originalStockData) {
      setStockData(originalStockData);
      setPeriodData(generatePeriodData(originalStockData));
      setCurrentPeriod('daily');
      message.success('已恢复到原始数据');
    }
  };

  /**
   * 下载数据到Excel
   */
  const handleDownloadExcel = () => {
    if (!stockData || stockData.length === 0) {
      message.warning('没有数据可下载');
      return;
    }

    // 构建CSV内容
    const headers = ['日期', '开盘价', '收盘价', '最高价', '最低价', '成交量(万)', '成交额(亿)', '涨跌幅(%)', '涨跌额'];
    const rows = stockData.map(item => [
      item.trade_date,
      item.open.toFixed(2),
      item.close.toFixed(2),
      item.high.toFixed(2),
      item.low.toFixed(2),
      (item.vol / 10000).toFixed(2),
      item.amount ? (item.amount * 1000 / 100000000).toFixed(2) : '-',
      item.change_pct ? item.change_pct.toFixed(2) : '-',
      item.change ? item.change.toFixed(2) : '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // 创建Blob对象
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${stockInfo ? stockInfo.name : '股票数据'}_${currentPeriod}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('数据下载成功');
  };

  return (
    <ConfigProvider locale={zhCN}>
      <Layout className="app-layout">
        <Header className="app-header">
          <h1 className="app-title">股票数据分析</h1>
        </Header>
        <Content className="app-content">
          <StockSearch 
            setStockData={handleStockDataChange}
            setStockInfo={setStockInfo}
            setLoading={setLoading}
            loading={loading}
          />
          {stockData && stockData.length > 0 && (
            <>
              {/* 操作按钮区域 */}
              <div style={{ margin: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Radio.Group 
                    value={currentPeriod} 
                    onChange={(e) => handlePeriodChange(e.target.value)}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="daily">日K</Radio.Button>
                    <Radio.Button value="weekly">周K</Radio.Button>
                    <Radio.Button value="monthly">月K</Radio.Button>
                    <Radio.Button value="day45">45天</Radio.Button>
                  </Radio.Group>
                </Space>
                <Space>
                  <Button 
                    onClick={handleResetData}
                  >
                    恢复原始数据
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadExcel}
                  >
                    下载数据
                  </Button>
                </Space>
              </div>
              <StockChart key={currentPeriod} data={stockData} stockInfo={stockInfo} onSimulatedData={handleSimulatedData} />
            </>
          )}
        </Content>
      </Layout>
    </ConfigProvider>
  );
}

export default App;
